// =========================
// FunnelPanel — v1.3.0 BL-038 Story 38.6 (Admin Portal — Funnel Visibility)
//
// Read-only AGGREGATE visibility into the conversion funnel: waitlist audience
// size, plan-intent distribution (which plan visitors chose — a non-billing
// demand signal), source / utm attribution, and trial/activation counts.
//
// Reads through lib/admin.listFunnelMetrics() → admin-get-funnel-metrics
// (service_role only; counts only — no PII, no payment data). This is
// OBSERVATION, not a CRM or a control surface.
//
// Rendered as a tab inside AdminDashboard; route gating
// (ProtectedRoute > AdminRoute) happens above the shell, and the function
// independently enforces the admin gate server-side (defense-in-depth).
// =========================

import React, { useEffect, useState, useCallback } from 'react';
import { listFunnelMetrics } from '../../lib/admin';

function tierLabel(key) {
  if (key === 'none') return 'No plan selected';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

const PLAN_ORDER = ['free', 'investor', 'professional', 'enterprise', 'none'];

const labelStyle = {
  display: 'block',
  fontSize: 'var(--text-sm)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-wide)',
  color: 'var(--color-steel)',
  marginBottom: 'var(--space-1)',
};

const statValueStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-4xl)',
  fontWeight: 'var(--weight-black)',
  color: 'var(--color-white)',
};

function Row({ label, value }) {
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-2) var(--space-4)',
      }}
    >
      <span style={{ color: 'var(--color-steel-lt)' }}>{label}</span>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontWeight: 'var(--weight-bold)',
          color: 'var(--color-white)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--weight-bold)',
          color: 'var(--color-white)',
          margin: 0,
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

export default function FunnelPanel() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: loadError } = await listFunnelMetrics();
    if (loadError) {
      setError(loadError.message || 'Could not load funnel metrics');
      setMetrics(null);
    } else {
      setMetrics(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div style={{ padding: 'var(--space-5)', color: 'var(--color-steel-lt)' }}>Loading…</div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        style={{
          padding: 'var(--space-4) var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--color-danger)',
          background: 'var(--color-danger-ghost)',
          color: 'var(--color-danger-lt)',
          fontWeight: 'var(--weight-semibold)',
        }}
      >
        {error}
      </div>
    );
  }

  const m = metrics || {};
  const planInterest = m.plan_interest || {};
  const bySource = m.by_source || {};
  const byUtm = m.by_utm_source || {};

  const planRows = PLAN_ORDER.filter((k) => k in planInterest);
  const sourceRows = Object.entries(bySource).sort((a, b) => b[1] - a[1]);
  const utmRows = Object.entries(byUtm).sort((a, b) => b[1] - a[1]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-5)' }}>
      {/* Headline counter */}
      <div className="card-stat" style={{ textAlign: 'left' }}>
        <span style={labelStyle}>Waitlist audience</span>
        <span style={statValueStyle}>{m.waitlist_total ?? 0}</span>
      </div>

      <Section title="Plan intent">
        {planRows.length === 0 ? (
          <div style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>No plan intent captured yet</div>
        ) : (
          planRows.map((key) => (
            <Row key={key} label={tierLabel(key)} value={planInterest[key]} />
          ))
        )}
      </Section>

      <Section title="Source">
        {sourceRows.length === 0 ? (
          <div style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>No sources yet</div>
        ) : (
          sourceRows.map(([key, n]) => <Row key={key} label={key} value={n} />)
        )}
      </Section>

      {utmRows.length > 0 && (
        <Section title="Campaign (utm_source)">
          {utmRows.map(([key, n]) => (
            <Row key={key} label={key} value={n} />
          ))}
        </Section>
      )}

      <Section title="Trials">
        <Row label="Total trials" value={m.trials_total ?? 0} />
        <Row label="Active trials" value={m.trials_active ?? 0} />
        <Row label="Converted after consumption" value={m.converted_after_consumption ?? 0} />
      </Section>
    </div>
  );
}
